import apiClient from './client';

export interface Lead {
  id: number;
  name: string; // Keep existing
  lead_name?: string; // Keep for backward compatibility
  first_name?: string; // Contact first name
  last_name?: string; // Contact last name (optional)
  stage: string;
  value: number | string; // Keep existing
  deal_value?: number | string; // New field
  currency?: 'USD' | 'INR';
  probability: number;
  expected_close_date: string;
  account_id?: number;
  primary_contact_id?: number; // New field
  /** From JOIN when loading leads – always current from contacts table */
  account_name?: string;
  primary_contact_first_name?: string;
  primary_contact_last_name?: string;
  owner_id: number; // Keep existing
  owner_name?: string; // Resolved from users for display/filter (admin/leadership)
  deal_owner_user_id?: number; // New field
  project_name?: string;
  service_line?: string;
  industry?: string;
  brand_type?: string;
  geography?: string;
  source?: string; // Keep existing
  lead_source?: string;
  created_at: string; // Keep existing
  date_created?: string; // New field
  last_contacted_at?: string; // New field
  category?: 'A' | 'B' | 'C';
  description?: string; // Keep existing
  notes?: string; // New field
  problem_statement?: string; // New field
  solution_positioning?: string; // New field
  competitor?: string; // New field
  urgency_score?: number; // 1-20
  next_action?: string;
  next_action_date?: string;
  close_plan_id?: number; // New field
  lost_reason?: string; // New field
  brand?: string; // Brand name
  decision_maker?: string; // Decision maker name
  decision_maker_contact_id?: number; // Reference to contact
  decision_maker_level?: string; // C-Level, VP, Director, Manager, etc.
  influencers?: string; // Influencers (text field)
  reason?: string; // General reason field
  won_date?: string; // Date when lead was won
  lost_date?: string; // Date when lead was lost
  updated_at: string;
}

export interface LeadsResponse {
  success: boolean;
  data: Lead[];
}

export const leadsApi = {
  getAll: async (): Promise<Lead[]> => {
    try {
      const response = await apiClient.get<LeadsResponse>('/leads');
      if (response.data && response.data.success && Array.isArray(response.data.data)) {
        return response.data.data;
      }
      // Fallback if response structure is different
      if (Array.isArray(response.data)) {
        return response.data;
      }
      console.error('Unexpected response structure:', response.data);
      return [];
    } catch (error: any) {
      console.error('Error fetching leads:', error);
      if (error.response) {
        console.error('Response status:', error.response.status);
        console.error('Response data:', error.response.data);
      }
      throw error;
    }
  },
  getById: async (id: number): Promise<Lead> => {
    const response = await apiClient.get<{ success: boolean; data: Lead }>(`/leads/${id}`);
    return response.data.data;
  },
  create: async (lead: Partial<Lead>): Promise<Lead> => {
    const response = await apiClient.post<{ success: boolean; data: Lead }>('/leads', lead);
    return response.data.data;
  },
  update: async (id: number, lead: Partial<Lead>): Promise<Lead> => {
    const response = await apiClient.patch<{ success: boolean; data: Lead }>(`/leads/${id}`, lead);
    return response.data.data;
  },
  delete: async (id: number): Promise<void> => {
    await apiClient.delete(`/leads/${id}`);
  },
};

